import { z } from 'zod'
import type { WemoloMenuOption } from '../../components/Wemolo'

// ============================================
// API / pagination
// ============================================

/** Backend cap. */
export const DETECTION_EXPLORER_MAX_PAGE_SIZE = 200

/** Rows requested per page in the table/gallery views; 20 keeps responses snappy. */
export const DETECTION_EXPLORER_PAGE_SIZE = 20

// ============================================
// Time window
// ============================================

/**
 * How far back the `from` field is allowed to point, in minutes. The backend hard-rejects `from`
 * older than 48h30m; we stop at 48h so request latency and client/server clock skew can't push a
 * boundary value past that limit — the backend's extra 30 min is the safety grace. Single source of
 * truth — UI clamping (Filters time-window), the submit-time Zod cross-field rule, and the "apply
 * image request" window all use this.
 */
export const DETECTION_EXPLORER_WINDOW_MINUTES = 48 * 60

/**
 * Safety margin (minutes) kept between `to` and the real "now". The backend rejects `to` in the
 * future with NO grace, so a client clock running a little ahead of the server would 400 every
 * request. Snapping `to` this far into the past absorbs that skew. Negligible against a 48h window.
 */
export const DETECTION_EXPLORER_NOW_GRACE_MINUTES = 2

/** How often Filters re-ticks the allowed window so picks don't go stale. */
export const DETECTION_EXPLORER_WINDOW_REFRESH_MS = 60_000

// Time-range presets shown as buttons in the Filters bar. Each sets `to = now` and `from = now − N`.
// 'custom' reveals the start/end datetime pickers instead. Default is 48h (the full allowed window).
export type TimePreset = '48h' | '24h' | '12h' | 'custom'

export const DETECTION_EXPLORER_TIME_PRESETS: { key: Exclude<TimePreset, 'custom'>; label: string; minutes: number }[] =
    [
        { key: '48h', label: '48h', minutes: 48 * 60 },
        { key: '24h', label: '24h', minutes: 24 * 60 },
        { key: '12h', label: '12h', minutes: 12 * 60 }
    ]

// Card-size scale for the gallery / group-by-LP grids. The actual min card widths live in CSS
// (.size* classes set --detection-card-min); this just enumerates the choices for the toggle.
export const CardSizeEnum = z.enum(['small', 'medium', 'large'])
export type CardSize = z.infer<typeof CardSizeEnum>

export const DETECTION_EXPLORER_CARD_SIZES: { key: CardSize; label: string; name: string }[] = [
    { key: 'small', label: 'S', name: 'Small' },
    { key: 'medium', label: 'M', name: 'Medium' },
    { key: 'large', label: 'L', name: 'Large' }
]

/**
 * How often the image-requests panel polls. Reconciles an optimistic row inserted on retrieve with
 * the real one (replacing it by request_uuid) and advances statuses (open → successful/…).
 */
export const DETECTION_EXPLORER_IMAGE_REQUESTS_POLL_MS = 30_000

// ============================================
// Domain values
// ============================================

// Backend sentinel for an unset category / country / direction / vehicle_type. It comes back on the
// response in place of null/"" (these four fields are now always non-empty strings), and is accepted
// on the request's matching list filters to match detections where that field is unset.
export const DETECTION_FIELD_UNSET = 'not_set'

export const isFieldUnset = (value: string | null | undefined): boolean => value === DETECTION_FIELD_UNSET

// Friendly labels for the unset sentinel. Category keeps the "not_categorized" wording carried over
// from the Lot Analysis Tool; the other three fields read as a generic "Not set".
export const DETECTION_FIELD_UNSET_LABEL = 'Not set'
export const DETECTION_CATEGORY_UNSET_LABEL = 'Uncategorized'
const UNSET_OPTION: WemoloMenuOption = { value: DETECTION_FIELD_UNSET, label: DETECTION_FIELD_UNSET_LABEL }
const CATEGORY_UNSET_OPTION: WemoloMenuOption = { value: DETECTION_FIELD_UNSET, label: DETECTION_CATEGORY_UNSET_LABEL }

export const DETECTION_EXPLORER_KNOWN_COUNTRIES = ['DE', 'AT', 'CH', 'IT', 'DK', 'PL', 'INT'] as const
export type DetectionExplorerKnownCountry = (typeof DETECTION_EXPLORER_KNOWN_COUNTRIES)[number]

export const DirectionEnum = z.enum(['IN', 'OUT', 'TURN', 'INV'])
export type Direction = z.infer<typeof DirectionEnum>

/** Map a raw API country (nullable, any string) to a plate-renderable known country, falling back to 'INT'. */
export const resolveKnownCountry = (country: string | null): DetectionExplorerKnownCountry =>
    DETECTION_EXPLORER_KNOWN_COUNTRIES.includes(country as DetectionExplorerKnownCountry)
        ? (country as DetectionExplorerKnownCountry)
        : 'INT'

// Country codes accepted by the filter. Most-used first, then the rest alphabetical.
export const CountryEnum = z.enum([
    'DE',
    'AT',
    'CH',
    'DK',
    'IT',
    'PL',
    'CZ',
    'FR',
    'AD',
    'AL',
    'AM',
    'AZ',
    'BA',
    'BE',
    'BG',
    'BY',
    'CY',
    'EE',
    'ES',
    'FI',
    'GB',
    'GE',
    'GI',
    'GR',
    'HR',
    'HU',
    'IE',
    'IS',
    'KZ',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'MD',
    'ME',
    'MK',
    'MT',
    'NL',
    'NO',
    'PT',
    'RO',
    'RS',
    'RU',
    'SE',
    'SI',
    'SK',
    'SM',
    'TR',
    'UA',
    'VA'
])
export type Country = z.infer<typeof CountryEnum>

export const DETECTION_EXPLORER_COUNTRY_OPTIONS: WemoloMenuOption[] = [
    ...CountryEnum.options.map((value) => ({ value, label: value })),
    UNSET_OPTION
]

// No "not set" option: INV (invalid) is already the catch-all direction, so it never comes back unset.
export const DETECTION_EXPLORER_DIRECTION_OPTIONS: WemoloMenuOption[] = DirectionEnum.options.map((value) => ({
    value,
    label: value
}))

export const CategoryEnum = z.enum([
    'match',
    'error_in_unmatched',
    'error_out_unmatched',
    'error_turn',
    'error_unknown',
    'error_truck',
    'error_non_valid_vehicle',
    'error_in_duplication',
    'error_out_duplication',
    'error_in_duplication_long',
    'error_out_duplication_long',
    'error_similar_plate',
    'error_low_lpr_confidence',
    'error_truncated_plate',
    'error_invalid',
    'error_invalid_trajectory',
    'error_no_movement',
    'error_filtered_device',
    'error_filtered_turn',
    'error_too_old_timestamp',
    'error_future_timestamp',
    'error_missing_lp'
])
export type Category = z.infer<typeof CategoryEnum>

const CATEGORY_LABELS: Record<Category, string> = {
    match: 'Match',
    error_in_unmatched: 'In Unmatched',
    error_out_unmatched: 'Out Unmatched',
    error_turn: 'Turn',
    error_unknown: 'Unknown',
    error_truck: 'Truck',
    error_non_valid_vehicle: 'Non-valid Vehicle',
    error_in_duplication: 'In Duplication',
    error_out_duplication: 'Out Duplication',
    error_in_duplication_long: 'In Duplication (long)',
    error_out_duplication_long: 'Out Duplication (long)',
    error_similar_plate: 'Similar Plate',
    error_low_lpr_confidence: 'Low LPR Confidence',
    error_truncated_plate: 'Truncated Plate',
    error_invalid: 'Invalid',
    error_invalid_trajectory: 'Invalid Trajectory',
    error_no_movement: 'No Movement',
    error_filtered_device: 'Filtered Device',
    error_filtered_turn: 'Filtered Turn',
    error_too_old_timestamp: 'Too Old Timestamp',
    error_future_timestamp: 'Future Timestamp',
    error_missing_lp: 'Missing LP'
}

export const DETECTION_EXPLORER_CATEGORY_OPTIONS: WemoloMenuOption[] = [
    ...CategoryEnum.options.map((value) => ({ value, label: CATEGORY_LABELS[value] })),
    CATEGORY_UNSET_OPTION
]

// ============================================
// Category groups
// ============================================
// Coarse buckets over the fine-grained categories so users can filter by intent rather than
// picking individual categories:
//   - correct:       non-error categories
//   - error_rate:    the fixed set that counts toward the lot's error rate
//   - error_other:   every other error_* category
//   - uncategorized: detections with no category (the "not_set" sentinel)
// A group filter expands to its member values and sends them via the `category` request field, so
// it's pure sugar over the per-category filter.

export const CategoryGroupEnum = z.enum(['correct', 'error_rate', 'error_other', 'uncategorized'])
export type CategoryGroup = z.infer<typeof CategoryGroupEnum>

const CATEGORY_GROUP_LABELS: Record<CategoryGroup, string> = {
    correct: 'Correct',
    error_rate: 'Error Rate',
    error_other: 'Error Other',
    uncategorized: 'Uncategorized'
}

const isErrorCategory = (category: Category): boolean => category.startsWith('error')

// Categories counted toward a lot's error rate (the LAT's `error_event_config`).
const CATEGORY_GROUP_ERROR_RATE_MEMBERS: Category[] = [
    'error_in_unmatched',
    'error_out_unmatched',
    'error_in_duplication_long',
    'error_out_duplication_long'
]

// Error groups derive from CategoryEnum so adding a category keeps them exhaustive without edits
// here; uncategorized maps to the unset sentinel (not a CategoryEnum value), hence the string[] type.
export const CATEGORY_GROUP_MEMBERS: Record<CategoryGroup, string[]> = {
    correct: CategoryEnum.options.filter((category) => !isErrorCategory(category)),
    error_rate: CATEGORY_GROUP_ERROR_RATE_MEMBERS,
    error_other: CategoryEnum.options.filter(
        (category) => isErrorCategory(category) && !CATEGORY_GROUP_ERROR_RATE_MEMBERS.includes(category)
    ),
    uncategorized: [DETECTION_FIELD_UNSET]
}

// The Category filter is a hierarchical dropdown: each group expands to its member categories.
// `value`s are the real category values (incl. the "not_set" sentinel) sent on the `category` field.
export type DetectionCategoryGroup = { key: CategoryGroup; label: string; options: WemoloMenuOption[] }

export const DETECTION_EXPLORER_CATEGORY_GROUPED: DetectionCategoryGroup[] = CategoryGroupEnum.options.map((key) => ({
    key,
    label: CATEGORY_GROUP_LABELS[key],
    options: CATEGORY_GROUP_MEMBERS[key].map((value) => ({
        value,
        label: value === DETECTION_FIELD_UNSET ? DETECTION_CATEGORY_UNSET_LABEL : CATEGORY_LABELS[value as Category]
    }))
}))

// Reverse index: category value -> its group, for tinting a detection by group at a glance.
const CATEGORY_TO_GROUP: Record<string, CategoryGroup> = Object.entries(CATEGORY_GROUP_MEMBERS).reduce(
    (acc, [group, members]) => {
        members.forEach((category) => {
            acc[category] = group as CategoryGroup
        })
        return acc
    },
    {} as Record<string, CategoryGroup>
)

// Visual tone for a detection's category, mirroring the Lot Analysis Tool's card colors but softened
// to the semantic highlight tokens: `correct` (green), errors that drive the lot's error rate as
// `critical` (red), other errors as `warning` (amber), and a null category as `uncategorized` (grey)
// so it's distinguishable even though it can't be filtered.
export type CategoryTone = 'correct' | 'warning' | 'critical' | 'uncategorized'

const CATEGORY_GROUP_TONE: Record<CategoryGroup, CategoryTone> = {
    correct: 'correct',
    error_rate: 'critical',
    error_other: 'warning',
    uncategorized: 'uncategorized'
}

export const categoryTone = (category: string | null | undefined): CategoryTone => {
    if (!category || isFieldUnset(category)) return 'uncategorized'
    const group = CATEGORY_TO_GROUP[category]
    return group ? CATEGORY_GROUP_TONE[group] : 'uncategorized'
}

export const VehicleTypeEnum = z.enum([
    'car',
    'motorcycle',
    'truck',
    'lcv',
    'trailer',
    'bicycle',
    'utility',
    'undetected',
    'van',
    'carry_on'
])
export type VehicleType = z.infer<typeof VehicleTypeEnum>

const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
    car: 'Car',
    motorcycle: 'Motorcycle',
    truck: 'Truck',
    lcv: 'LCV',
    trailer: 'Trailer',
    bicycle: 'Bicycle',
    utility: 'Utility',
    undetected: 'Undetected',
    van: 'Van',
    carry_on: 'Carry-on'
}

export const DETECTION_EXPLORER_VEHICLE_TYPE_OPTIONS: WemoloMenuOption[] = [
    ...VehicleTypeEnum.options.map((value) => ({ value, label: VEHICLE_TYPE_LABELS[value] })),
    UNSET_OPTION
]

export type DetectionExplorerFilterKey =
    | 'license_plate'
    | 'category'
    | 'vehicle_type'
    | 'direction'
    | 'country'
    | 'lpr_conf'
    | 'lpness'
    | 'device'
    | 'detection_uuid'

type ArrayRequestKey =
    | 'license_plate'
    | 'category'
    | 'vehicle_type'
    | 'direction'
    | 'country'
    | 'device_uuids'
    | 'device_ids'
    | 'detection_uuids'

type FilterInput =
    /** Single text input. Multiple chips of this filter are aggregated into the request array. */
    | { type: 'singleText'; requestKey: ArrayRequestKey }
    /** Multi-select dropdown. One chip holds the whole array. */
    | { type: 'multiSelect'; requestKey: ArrayRequestKey; options: WemoloMenuOption[] }
    | {
          type: 'numberRange'
          minRequestKey: 'lpr_conf_min' | 'lpness_min'
          maxRequestKey: 'lpr_conf_max' | 'lpness_max'
          /** Multiplier between displayed value and stored value (e.g. 100 → user types 0–100, store gets 0–1). */
          displayScale: number
      }

export type DetectionExplorerFilterDefinition = {
    key: DetectionExplorerFilterKey
    label: string
    operator: string
    /** When true, the Add Filter menu keeps this key available even after a chip exists. */
    allowMultipleChips: boolean
    input: FilterInput
}

export const DETECTION_EXPLORER_FILTER_DEFINITIONS: Record<
    DetectionExplorerFilterKey,
    DetectionExplorerFilterDefinition
> = {
    device: {
        key: 'device',
        label: 'Device',
        operator: 'is',
        allowMultipleChips: false,
        // Filters by the numeric device_id via the request's `device_ids` field. Options are
        // injected at runtime — one per device_id learned from loaded detections (see
        // useDeviceFilterOptions), since ids exist only on detection rows, not on lot/station data.
        input: { type: 'multiSelect', requestKey: 'device_ids', options: [] }
    },
    license_plate: {
        key: 'license_plate',
        label: 'License Plate',
        operator: 'contains',
        allowMultipleChips: true,
        input: { type: 'singleText', requestKey: 'license_plate' }
    },
    category: {
        key: 'category',
        label: 'Category',
        operator: 'is',
        allowMultipleChips: false,
        // Rendered as a hierarchical group→category dropdown (see CategoryFilterDropdown); the chip
        // stores the selected category values, sent directly on the `category` request field.
        input: {
            type: 'multiSelect',
            requestKey: 'category',
            options: DETECTION_EXPLORER_CATEGORY_OPTIONS
        }
    },
    vehicle_type: {
        key: 'vehicle_type',
        label: 'Vehicle Type',
        operator: 'is',
        allowMultipleChips: false,
        input: {
            type: 'multiSelect',
            requestKey: 'vehicle_type',
            options: DETECTION_EXPLORER_VEHICLE_TYPE_OPTIONS
        }
    },
    direction: {
        key: 'direction',
        label: 'Direction',
        operator: 'is',
        allowMultipleChips: false,
        input: {
            type: 'multiSelect',
            requestKey: 'direction',
            options: DETECTION_EXPLORER_DIRECTION_OPTIONS
        }
    },
    country: {
        key: 'country',
        label: 'Country',
        operator: 'is',
        allowMultipleChips: false,
        input: {
            type: 'multiSelect',
            requestKey: 'country',
            options: DETECTION_EXPLORER_COUNTRY_OPTIONS
        }
    },
    detection_uuid: {
        key: 'detection_uuid',
        label: 'Detection UUID',
        operator: 'is',
        // Exact-match on detection_uuid; add a chip per id to filter on several at once.
        allowMultipleChips: true,
        input: { type: 'singleText', requestKey: 'detection_uuids' }
    },
    lpr_conf: {
        key: 'lpr_conf',
        label: 'LPR Conf. %',
        operator: 'between',
        allowMultipleChips: false,
        input: {
            type: 'numberRange',
            minRequestKey: 'lpr_conf_min',
            maxRequestKey: 'lpr_conf_max',
            displayScale: 100
        }
    },
    lpness: {
        key: 'lpness',
        label: 'LP-ness %',
        operator: 'between',
        allowMultipleChips: false,
        input: {
            type: 'numberRange',
            minRequestKey: 'lpness_min',
            maxRequestKey: 'lpness_max',
            displayScale: 100
        }
    }
}

export const DETECTION_EXPLORER_FILTER_OPTIONS: WemoloMenuOption[] = Object.values(
    DETECTION_EXPLORER_FILTER_DEFINITIONS
).map(({ key, label }) => ({ value: key, label }))

export type DetectionExplorerColumn = { key: string; label: string }

export const DETECTION_EXPLORER_COLUMNS: DetectionExplorerColumn[] = [
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'country', label: 'Country' },
    { key: 'license_plate', label: 'License Plate' },
    { key: 'station', label: 'Station' },
    { key: 'direction', label: 'Direction' },
    { key: 'category', label: 'Category' },
    { key: 'lpr_conf', label: 'LPR Conf.' },
    { key: 'media', label: 'Media' }
]

// ============================================
// User-facing text
// ============================================

export const DETECTION_EXPLORER_TEXT = {
    emptyValue: '—',
    // Display label for the unset sentinel: "not_categorized" for category, "Not set" elsewhere.
    uncategorized: DETECTION_CATEGORY_UNSET_LABEL,
    notSet: DETECTION_FIELD_UNSET_LABEL,
    onDevice: 'On Device',
    requested: 'Requested',
    incompleteDetectionIdTitle: 'Incomplete Detection ID',
    incompleteDetectionIdHint: 'Enter a full detection UUID to filter by it.',
    loadErrorTitle: "Couldn't load detections",
    // Shown when the request 400s because the tab sat open long enough for the time window to drift
    // past the backend's 48h limit. A refresh re-snaps the window.
    loadErrorStaleWindow:
        'This view has been open a while, so the time window expired. Refresh to load the latest detections.',
    refresh: 'Refresh',
    emptyState: 'No detections to display.',
    // Shown when the selected lot has no camera devices, so there's nothing to query detections for.
    noDevicesTitle: 'No devices on this lot',
    noDevicesHint: 'This lot has no camera devices, so there are no detections to show.',
    viewFullSize: 'View full size',
    media: {
        // Explains the three R/C/P badge colours; shown in the Media column header info tip.
        legend: 'Each badge is an image type — R: render, C: context, P: plate.\nGreen: available · Yellow: requested, awaiting upload · Grey: on device, not requested.',
        legendLabel: 'Media states'
    },
    imageRequests: {
        title: 'Image requests',
        searchPlaceholder: 'Search by ID or requester…',
        empty: 'No recent image requests.',
        noMatches: 'No requests match your search.',
        unknownRequester: 'Unknown',
        collapse: 'Hide image requests',
        refresh: 'Refresh image requests',
        statusOpen: 'Open',
        statusPartial: 'Partial',
        statusSuccessful: 'Done',
        statusFailed: 'Failed',
        // Per-row "show the detections this request targeted" action.
        apply: 'Apply',
        applyTitle: 'Show the detections this request targeted',
        // Short summary of what a request's detection_selector targets.
        selectorDetections: (count: number) => `${count} detection${count === 1 ? '' : 's'}`,
        selectorTimerange: 'Time range',
        // The dismissible indicator shown in the active-filters strip while a request is applied.
        appliedLabel: 'Image request',
        clearApplied: 'Clear applied image request'
    },
    detail: {
        recognitionConfidence: 'Recognition confidence',
        lprConfidence: 'LPR confidence',
        lpness: 'LP-ness',
        lprBelowThreshold: 'Below 0.50 — plate unreadable, flagged invalid',
        lpnessHint: 'Likelihood a plate is present in the crop',
        detection: 'Detection',
        detectionId: 'Detection ID',
        deviceId: 'Device ID',
        deviceUuid: 'Device UUID',
        copyDetectionId: 'Copy detection ID',
        copyDeviceUuid: 'Copy device UUID',
        tabRender: 'Render',
        tabContext: 'Raw',
        tabPlate: 'Plate',
        // Hover hints on the image tabs, explaining what each artifact is.
        tabRenderHint: 'Full detection render',
        tabContextHint: 'Region of interest',
        tabPlateHint: 'Plate crop',
        contextUnavailable: 'Raw image not available yet',
        plateUnavailable: 'Plate crop not available',
        download: 'Download',
        downloading: 'Downloading…'
    },
    pagination: {
        previous: 'Previous',
        next: 'Next',
        first: 'First',
        last: 'Last',
        ellipsis: '…'
    },
    actionBar: {
        itemsSelected: 'items selected',
        clearSelection: 'Clear',
        viewing: 'Viewing',
        of: 'of',
        platesUnit: ' plates',
        // Shown in place of "Viewing X–Y of Z" while a client-side result filter is active, since the
        // total match count isn't known until everything is scanned.
        showing: 'Showing',
        scanned: 'scanned',
        retrieve: 'Retrieve',
        retrieving: 'Retrieving…',
        retrieveRequested: (requestId: string) => `Image retrieval requested (request ${requestId})`,
        retrieveFailed: 'Could not request images',
        export: 'Export',
        exporting: 'Exporting…'
    },
    // Display labels for the backend image_types (render/roi/lp), shared by the export and
    // retrieve image-type pickers.
    imageTypes: {
        title: 'Image types',
        render: { label: 'Render', description: 'Full detection render' },
        roi: { label: 'Raw', description: 'Region of interest' },
        lp: { label: 'License plate', description: 'Plate crop' }
    },
    exportModal: {
        title: 'Export',
        selectedDetections: 'selected detections',
        allDetections: 'detections',
        noImagesHint: 'No images available',
        cancel: 'Cancel',
        confirm: 'Export',
        detectionsOnly: {
            title: 'Detections only',
            description: 'Download a CSV of detection data'
        },
        imagesOnly: {
            title: 'Images only',
            description: 'Download a ZIP of selected image types'
        },
        all: {
            title: 'Detections & images',
            description: 'Download a ZIP with a CSV and selected image types'
        },
        // Shown when a selection mixes detections that have images with ones that don't: the
        // imageless rows are silently skipped from a ZIP export unless retrieved first.
        partialImages: (missing: number, total: number) =>
            `${missing} of ${total} selected detections have no images yet and will be skipped. Retrieve their images first to include them.`,
        // Full-export variant: only the current page is known, so we can't give exact counts.
        partialImagesEstimate:
            'Some detections have no images yet and will be skipped. Retrieve their images first to include them.',
        // Appended when some of the missing rows are already covered by an ongoing retrieval.
        partialImagesPending: (pending: number) => `${pending} ${pending === 1 ? 'is' : 'are'} already being retrieved.`
    },
    retrieveModal: {
        title: 'Retrieve High-Res Assets',
        intro: 'You are about to request full-resolution media from the edge devices for',
        detections: 'detections',
        estimatedTransfer: 'Estimated Transfer',
        cancel: 'Cancel',
        confirm: 'Start Retrieval'
    },
    filters: {
        addFilter: 'Add Filter',
        clearAll: 'Clear all',
        removeFilter: 'Remove filter',
        selectValues: 'Select…',
        selectAll: 'Select all',
        nSelected: (n: number) => `${n} selected`,
        expandGroup: 'Expand group',
        viewModeGroup: 'View mode',
        listView: 'List view',
        galleryView: 'Gallery view',
        groupByPlateView: 'Group by license plate view'
    },
    timeFrame: {
        start: 'Start',
        end: 'End',
        custom: 'Custom',
        rangeLabel: 'Time range'
    },
    table: {
        actions: 'Actions'
    },
    // Client-side result-filter checkboxes shown above the views. "With images only" applies to every
    // view; the two LP-case filters only show in Group-by-LP.
    resultFilters: {
        withImagesOnly: 'With images only',
        lpWithImage: 'Has image',
        lpWithError: 'Has error',
        cardSizeLabel: 'Card size'
    },
    groupByLP: {
        detection: 'detection',
        detections: 'detections',
        latest: 'latest'
    }
} as const
